/**
 * Mega Menu Component
 *
 * Handles the functionality for the navigation mega menu
 * with pane swapping between different category levels.
 */
class MegaMenu extends HTMLElement {
  /**
   * Initialize the component when connected to the DOM
   */
  connectedCallback() {
    this.levelOneTriggers = this.querySelectorAll('[data-level-one-trigger]');
    this.levelTwoTriggers = this.querySelectorAll('[data-level-two-trigger]');
    this.levelOnePanes = this.querySelectorAll('[data-level-one-pane]');
    this.levelTwoPanes = this.querySelectorAll('[data-level-two-pane]');

    this.addEventListeners();
  }

  /**
   * Add event listeners to the triggers
   */
  addEventListeners() {
    this.levelOneTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleLevelOneClick.bind(this));
    });

    // Add both hover and click listeners for level two triggers
    this.addEventListener('mouseenter', this.handleLevelTwoHover.bind(this), true);
    this.addEventListener('click', this.handleLevelTwoClick.bind(this), true);

    this.activePinnedPane = null; // Track the single active pinned pane
  }

  /**
   * Handle click on level one trigger
   * @param {Event} event - The click event
   */
  handleLevelOneClick(event) {
    event.preventDefault();
    const categoryId = event.currentTarget.getAttribute('data-level-one-trigger');
    this.showLevelOnePane(categoryId);
  }

  /**
   * Handle hover on level two trigger
   * @param {Event} event - The mouseenter event
   */
  handleLevelTwoHover(event) {
    const trigger = event.target.closest('[data-level-two-trigger]');
    if (!trigger) return;

    this.hideLevelTwoPane(this.activePinnedPane);

    const categoryId = trigger.getAttribute('data-level-two-trigger');
    this.showLevelTwoPane(categoryId, false); // false = hover mode
  }

  /**
   * Handle click on level two trigger
   * @param {Event} event - The click event
   */
  handleLevelTwoClick(event) {
    const trigger = event.target.closest('[data-level-two-trigger]');
    if (!trigger) return;

    event.preventDefault();
    const categoryId = trigger.getAttribute('data-level-two-trigger');

    if (this.activePinnedPane === categoryId) {
      // If clicking the currently active pane, unpin it
      this.activePinnedPane = null;
      this.hideLevelTwoPane(categoryId);
    } else {
      // Hide the previously pinned pane if there was one
      if (this.activePinnedPane) {
        this.hideLevelTwoPane(this.activePinnedPane);
      }

      // Pin this pane open as the new active one
      this.activePinnedPane = categoryId;
      this.showLevelTwoPane(categoryId, true); // true = click mode
    }
  }

  /**
   * Show the level one pane for the selected category
   * @param {string} categoryId - The ID of the category to show
   */
  showLevelOnePane(categoryId) {
    // Clear active pinned pane when switching level one categories
    this.activePinnedPane = null;

    // Close all level two panes first
    this.hideAllLevelTwoPanes();

    // Update level one triggers
    this.levelOneTriggers.forEach((trigger) => {
      const isSelected = trigger.getAttribute('data-level-one-trigger') === categoryId;
      trigger.setAttribute('aria-expanded', isSelected);

      // Add visual feedback
      if (isSelected) {
        trigger.classList.add('underline');
      } else {
        trigger.classList.remove('underline');
      }
    });

    // Update level one panes
    this.levelOnePanes.forEach((pane) => {
      if (pane.getAttribute('data-level-one-pane') === categoryId) {
        pane.setAttribute('aria-hidden', 'false');
      } else {
        pane.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * Show the level two pane for the selected category
   * @param {string} categoryId - The ID of the category to show
   * @param {boolean} isClick - Whether this was triggered by a click (for pinning)
   */
  showLevelTwoPane(categoryId, isClick = false) {
    // Update level two triggers within the current level one pane
    const activeLevelOnePane = this.querySelector('[data-level-one-pane][aria-hidden="false"]');
    if (activeLevelOnePane) {
      const levelTwoTriggersInPane = activeLevelOnePane.querySelectorAll(
        '[data-level-two-trigger]'
      );
      levelTwoTriggersInPane.forEach((trigger) => {
        const isSelected = trigger.getAttribute('data-level-two-trigger') === categoryId;
        trigger.setAttribute('aria-expanded', isSelected);

        // Add visual feedback for selected trigger
        if (isSelected) {
          trigger.classList.add('underline');
          // Add pinned visual state if clicked
          if (isClick) {
            trigger.classList.add('underline');
          }
        } else {
          // Only remove hover state if not the active pinned pane
          if (this.activePinnedPane !== trigger.getAttribute('data-level-two-trigger')) {
            trigger.classList.remove('underline');
          }
        }
      });

      // Update level two panes within the same level one pane (second column)
      const levelTwoPanesInPane = activeLevelOnePane.querySelectorAll('[data-level-two-pane]');
      levelTwoPanesInPane.forEach((pane) => {
        if (pane.getAttribute('data-level-two-pane') === categoryId) {
          pane.setAttribute('aria-hidden', 'false');
          pane.classList.remove('hidden');
        } else {
          // Only hide if not the active pinned pane
          const paneId = pane.getAttribute('data-level-two-pane');
          if (this.activePinnedPane !== paneId) {
            pane.setAttribute('aria-hidden', 'true');
            pane.classList.add('hidden');
          }
        }
      });
    }
  }

  /**
   * Hide the level two pane for the selected category
   * @param {string} categoryId - The ID of the category to hide
   */
  hideLevelTwoPane(categoryId) {
    const activeLevelOnePane = this.querySelector('[data-level-one-pane][aria-hidden="false"]');
    if (activeLevelOnePane) {
      // Update trigger visual state
      const trigger = activeLevelOnePane.querySelector(`[data-level-two-trigger="${categoryId}"]`);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('underline');
      }

      // Hide the pane
      const pane = activeLevelOnePane.querySelector(`[data-level-two-pane="${categoryId}"]`);
      if (pane) {
        pane.setAttribute('aria-hidden', 'true');
        pane.classList.add('hidden');
      }
    }
  }

  /**
   * Hide all level two panes
   */
  hideAllLevelTwoPanes() {
    // Hide triggers in all level one panes
    const allLevelTwoTriggers = this.querySelectorAll('[data-level-two-trigger]');
    allLevelTwoTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('underline');
    });

    // Hide panes in all level one panes
    const allLevelTwoPanes = this.querySelectorAll('[data-level-two-pane]');
    allLevelTwoPanes.forEach((pane) => {
      pane.setAttribute('aria-hidden', 'true');
      pane.classList.add('hidden');
    });
  }

  /**
   * Hide all panes
   */
  hideAllPanes() {
    // Hide level one panes
    this.levelOneTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.classList.remove('underline');
    });

    this.levelOnePanes.forEach((pane) => {
      pane.setAttribute('aria-hidden', 'true');
    });

    // Hide level two panes
    this.hideAllLevelTwoPanes();
  }
}

customElements.define('mega-menu', MegaMenu);
